import type { RequestProjectsParams } from '@/features/project/_hooks/project';
import type {
  AmountSearchConditionSchema,
  ProjectSearchFormType,
} from '@/features/search/_models/projects-search';
import { getActivityIndicatorSearchParams } from '@/features/search/_utils/activity-indicator-conditions';
import { getInitialValueOfOptionalFields } from '@/features/search/_utils/projects-search';
import {
  BigCalculator,
  isPS,
  logger,
  lowerCamelStringToSnake,
  stringToBoolean,
} from '@/utils';
import type { z } from 'zod';
import type { ProjectsPageSearchQueryStates } from '../_hooks/search-projects/search-query-states';

type ProjectFilterParams = {
  isDisplayPolicyLevel?: boolean;
  sorting: string;
  searchQueryStates: ProjectsPageSearchQueryStates;
  groupFiscalYear: string | undefined;
};
const SPLIT_STRING = ',';

export const convertRequestParams = ({
  isDisplayPolicyLevel,
  searchQueryStates,
  sorting,
  groupFiscalYear,
}: ProjectFilterParams): RequestProjectsParams => {
  const {
    sheetType,
    isCandidateOfInspectionByExternalExpert,
    isPublished,
    page,
    pageSize,
    startYear,
    endYear,
    ordering,
    categories,
    implementationMethods,
    inspectionByExternalExpertReviewType,
    isSearchTargetProjectName,
    accountingTypeIds,
    cascader,
    accountingType,
    fundSheetType,
    corporateNumber,
    lastImplementedFiscalYearRangeMax,
    lastImplementedFiscalYearRangeMin,
    projectNumbers,
    organizationIds,
    policyIds,
    otherResponsibleOrganizationIds,
    expenditureBudgetPurposeIds,
    q,
    formulaConditions,
    indicatorKeyword,
    indicatorKeywordSearchOnlyTitleAndDescription,
    indicatorType,
    indicatorTerm,
    indicatorDataNatureType,
    hasOnlyQualitativeIndicator,
  } = searchQueryStates;

  const initialValueOfOptionalFields = getInitialValueOfOptionalFields({
    searchQueryStates,
    initialFiscalYearByGroup: groupFiscalYear,
  });

  const activityIndicatorSearchParams = getActivityIndicatorSearchParams({
    sheetType,
    fundSheetType,
    indicatorKeyword,
    indicatorKeywordSearchOnlyTitleAndDescription,
    indicatorType,
    indicatorTerm,
    indicatorDataNatureType,
    hasOnlyQualitativeIndicator,
  });

  const allParams: RequestProjectsParams = {
    sheetType,
    isCandidateOfInspectionByExternalExpert: stringToBoolean(
      isCandidateOfInspectionByExternalExpert,
    ),
    startYear: startYear?.toString() ?? null,
    endYear: endYear?.toString() ?? null,
    isPublished: stringToBoolean(isPublished),
    page: page.toString(),
    pageSize: pageSize.toString(),
    ordering: isDisplayPolicyLevel ? sorting : ordering,
    categories: categories?.join(SPLIT_STRING) ?? null,
    implementationMethods: implementationMethods?.join(SPLIT_STRING) ?? null,
    inspectionByExternalExpertReviewType:
      inspectionByExternalExpertReviewType?.join(SPLIT_STRING) ?? null,
    textSearchCriteria: isSearchTargetProjectName ? 'name' : null,
    accountingTypeIds: accountingTypeIds?.join(SPLIT_STRING) ?? null,
    fiscalYear: initialValueOfOptionalFields.fiscalYear,
    skipAdministrativeReformTeamEvaluation:
      initialValueOfOptionalFields.skipAdministrativeReformTeamEvaluation
        ? stringToBoolean(
            initialValueOfOptionalFields.skipAdministrativeReformTeamEvaluation,
          )
        : null,
    cascader,
    accountingType,
    fundSheetType,
    corporateNumber,
    lastImplementedFiscalYearRangeMax,
    lastImplementedFiscalYearRangeMin,
    projectNumbers,
    organizationIds: organizationIds?.join(SPLIT_STRING) ?? null,
    policyIds: policyIds?.join(SPLIT_STRING) ?? null,
    otherResponsibleOrganizationIds:
      otherResponsibleOrganizationIds?.join(SPLIT_STRING) ?? null,
    expenditureBudgetPurposeIds:
      expenditureBudgetPurposeIds?.join(SPLIT_STRING) ?? null,
    q,
    formulaConditions: formulaConditions
      ? (() => {
          // 年度が設定されていない条件はnullを返す
          const validConditions = formulaConditions.filter((v) => {
            if (v.conditionType === 'threshold') {
              return v.baseAmountYear !== null;
            }
            if (v.conditionType === 'trend') {
              return v.baseAmountYear !== null && v.targetAmountYear !== null;
            }
            return false;
          });

          return validConditions.length > 0
            ? JSON.stringify(
                validConditions.map((v) => {
                  const keys = Object.keys(
                    v,
                  ) as (keyof AmountSearchCondition)[];

                  return keys.reduce<Record<string, any>>((acc, key) => {
                    const obj = convertFormulaConditions(v, key);
                    Object.assign(acc, obj);
                    return acc;
                  }, {});
                }),
              )
            : null;
        })()
      : null,
    // 効果発現経路（指標）検索
    ...((activityIndicatorSearchParams as any) ?? {}),
  };
  // nullは除外
  return Object.fromEntries(
    Object.entries(allParams).filter(
      ([_, value]) => value !== null && value !== undefined,
    ),
  ) as RequestProjectsParams;
};

type AmountSearchCondition = z.infer<
  typeof AmountSearchConditionSchema.element
>;
/**
 * 数式検索条件のスネークケースへの変換処理を行う関数
 */
const convertFormulaConditions = (
  formulaCondition: AmountSearchCondition,
  key: keyof AmountSearchCondition,
) => {
  const value = (() => {
    switch (key) {
      case 'targetAmountType':
      case 'baseAmountType':
      case 'trendCondition':
      case 'conditionType': {
        const defaultValue = formulaCondition[key];
        return defaultValue ? lowerCamelStringToSnake(defaultValue) : null;
      }
      case 'baseAmountValueFrom':
      case 'baseAmountValueTo':
      case 'trendConditionValue':
      case 'baseAmountYear':
      case 'targetAmountYear':
      case 'baseAmountValueIsnull': {
        return formulaCondition[key];
      }
      default: {
        const check: never = key;
        logger.error('Unexpected target', check);
      }
    }
  })();

  return {
    [lowerCamelStringToSnake(key)]: value,
  };
};

/**
 * 金額検索条件の処理と金額変換を行う関数
 * @param formulaConditions - 変換対象の条件配列
 * @param options - オプション設定
 * @param options.rsOnly - RSシートのみの処理かどうか（デフォルト: true）
 * @param options.direction - 変換方向（'multiply': 1000倍、'divide': 1000で除算）
 * @returns 変換後の条件配列またはnull
 */
export const processFormulaConditionsAmount = (
  formulaConditions: AmountSearchCondition[] | null | undefined,
  options: {
    rsOnly?: boolean;
    direction: 'multiply' | 'divide';
  },
): AmountSearchCondition[] | null => {
  const { rsOnly = true, direction } = options;

  // 後方互換性のため、multiplyでrsOnlyがfalseの場合はnullを返す
  if (direction === 'multiply' && rsOnly === false) {
    return null;
  }

  if (!formulaConditions?.length) {
    return null;
  }

  // RSシートの場合のみ年度でフィルタリング
  const validConditions = rsOnly
    ? formulaConditions.filter((v) => {
        if (v.conditionType === 'threshold') {
          return v.baseAmountYear !== null;
        }
        if (v.conditionType === 'trend') {
          return v.baseAmountYear !== null && v.targetAmountYear !== null;
        }
        return false;
      })
    : formulaConditions;

  if (validConditions.length === 0) {
    return null;
  }

  /**
   * 金額値を条件に応じて変換する内部関数
   */
  const convertAmountValue = (
    value: number | null | undefined,
    skipConversion: boolean,
  ): number | null => {
    if (skipConversion) {
      return value ?? null;
    }
    if (typeof value !== 'number') {
      return null;
    }
    return direction === 'multiply'
      ? BigCalculator.multiply(value, 1000)
      : BigCalculator.divide(value, 1000);
  };

  return validConditions.map((condition) => ({
    ...condition,
    baseAmountValueFrom: convertAmountValue(
      condition.baseAmountValueFrom,
      condition.baseAmountType === 'executionRate',
    ),
    baseAmountValueTo: convertAmountValue(
      condition.baseAmountValueTo,
      condition.baseAmountType === 'executionRate',
    ),
    trendConditionValue: convertAmountValue(
      condition.trendConditionValue,
      condition.trendCondition === 'decreaseGtePercent' ||
        condition.trendCondition === 'increaseGtePercent',
    ),
  }));
};

const getIsPsParams = ({
  nestedExpenditureBudgetPurposeIds,
  nestedPolicyIds,
  nestedOrganizationIds,
  nestedOtherResponsibleOrganizationIds,
  sheetType,
}: ProjectSearchFormType) => {
  if (!isPS()) {
    return {
      expenditureBudgetPurposeIds: null,
      policyIds: null,
      organizationIds: null,
      otherResponsibleOrganizationIds: null,
    };
  }
  const rsOnly = sheetType === 'RS';

  return {
    expenditureBudgetPurposeIds:
      rsOnly && nestedExpenditureBudgetPurposeIds?.length
        ? nestedExpenditureBudgetPurposeIds.flatMap((item) => item.at(-1) ?? [])
        : null,
    policyIds:
      rsOnly && nestedPolicyIds?.length
        ? nestedPolicyIds.flatMap((item) => item.at(-1) ?? [])
        : null,
    organizationIds: nestedOrganizationIds?.length
      ? nestedOrganizationIds?.flatMap((item) => item.at(-1) ?? [])
      : null,
    otherResponsibleOrganizationIds:
      nestedOtherResponsibleOrganizationIds?.length
        ? nestedOtherResponsibleOrganizationIds.flatMap(
            (item) => item.at(-1) ?? [],
          )
        : null,
  };
};

/**
 * APIから返ってきた金額検索条件を画面表示用に1000で除算する関数（後方互換用）
 * 千円単位で保存されている金額を円単位に戻す
 */

export const convertInputValueToRequestParams = (
  values: ProjectSearchFormType,
  newCascadeId: string | undefined | null,
): ProjectsPageSearchQueryStates => {
  const { sheetType, fundSheetType, period, lastImplementedFiscalYearPeriod } =
    values;

  const fiscalYear = values.fiscalYear?.year() ?? null;

  const rsOnly = sheetType === 'RS';
  const ssOnly = sheetType === 'SS';
  const ksOnly = sheetType === 'KS';

  const withoutKs = sheetType !== 'KS';

  const isReviewTargetRs =
    rsOnly && values.skipAdministrativeReformTeamEvaluation !== 'true';

  const standardKs = ksOnly && fundSheetType === 'standard';
  const localGovernmentKs = ksOnly && fundSheetType === 'local-government';

  const formulaConditions = processFormulaConditionsAmount(
    values.formulaConditions,
    {
      rsOnly,
      direction: 'multiply',
    },
  );

  const activityIndicatorSearchParams = getActivityIndicatorSearchParams({
    sheetType,
    fundSheetType,
    indicatorKeyword: values.indicatorKeyword,
    indicatorKeywordSearchOnlyTitleAndDescription:
      values.indicatorKeywordSearchOnlyTitleAndDescription,
    indicatorType: values.indicatorType,
    indicatorTerm: values.indicatorTerm,
    indicatorDataNatureType: values.indicatorDataNatureType,
    hasOnlyQualitativeIndicator: values.hasOnlyQualitativeIndicator,
  });

  return {
    page: 1,
    pageSize: 30,
    sheetType,
    fiscalYear: fiscalYear?.toString() ?? null,
    fundSheetType: ksOnly ? fundSheetType : null,
    q: values.q ?? null,
    isSearchTargetProjectName: (values.q
      ? (values.isSearchTargetProjectName ?? false)
      : null) as any,
    cascader: newCascadeId ?? null,
    corporateNumber:
      standardKs || isReviewTargetRs || ssOnly ? values.corporateNumber : null,
    categories:
      withoutKs && values.categories?.length ? values.categories : null,
    implementationMethods:
      isReviewTargetRs && values.implementationMethods?.length
        ? values.implementationMethods
        : null,
    accountingType: rsOnly ? (values.accountingType ?? null) : null,
    accountingTypeIds:
      rsOnly && values.accountingTypeIds?.length
        ? values.accountingTypeIds
        : null,
    skipAdministrativeReformTeamEvaluation: (rsOnly && !isPS()
      ? values.skipAdministrativeReformTeamEvaluation === null
        ? 'all'
        : values.skipAdministrativeReformTeamEvaluation
      : null) as any,
    projectNumbers: values.projectNumbers ?? null,
    isPublished: !isPS() ? values.isPublished : null,
    startYear: localGovernmentKs ? null : (period?.[0]?.year() ?? null),
    endYear: localGovernmentKs ? null : (period?.[1]?.year() ?? null),
    lastImplementedFiscalYearRangeMin: isReviewTargetRs
      ? (lastImplementedFiscalYearPeriod?.[0]?.year() ?? null)
      : null,
    lastImplementedFiscalYearRangeMax: isReviewTargetRs
      ? (lastImplementedFiscalYearPeriod?.[1]?.year() ?? null)
      : null,
    inspectionByExternalExpertReviewType: isReviewTargetRs
      ? values.inspectionByExternalExpertReviewType
      : null,
    isCandidateOfInspectionByExternalExpert:
      isReviewTargetRs && !isPS()
        ? (values.isCandidateOfInspectionByExternalExpert ?? null)
        : null,
    ordering: null,
    ...getIsPsParams(values),
    formulaConditions,
    // 効果発現経路（指標）検索
    ...((activityIndicatorSearchParams as any) ?? {}),
    // group
    groupInProjectsOrdering: null,
    groupInProjectsPage: 1,
  };
};
